const nextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

export const settle = async (frames = 2) => {
  for (let index = 0; index < frames; index += 1) await nextFrame();
};

export const waitForImages = (root: ParentNode) =>
  Promise.all(
    Array.from(root.querySelectorAll('img')).map((image) =>
      image.complete ? undefined : image.decode().catch(() => undefined)
    )
  );

const PRINT_FRAME_SRCDOC =
  '<!doctype html><html><head><meta charset="utf-8"></head><body></body></html>';
const AFTERPRINT_FALLBACK_MS = 120_000;

export type PrintFrame = {
  document: Document;
  mountNode: HTMLElement;
  syncStyles: () => Promise<void>;
  print: (title: string) => Promise<void>;
  dispose: () => void;
};

const waitForLink = (link: HTMLLinkElement) =>
  new Promise<void>((resolve) => {
    if (link.sheet) {
      resolve();
      return;
    }
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
  });

const serializeRules = (sheet: CSSStyleSheet): string | null => {
  try {
    return Array.from(sheet.cssRules)
      .map((rule) => rule.cssText)
      .join('\n');
  } catch {
    return null;
  }
};

const snapshotCanvases = (target: Document) => {
  for (const canvas of Array.from(target.querySelectorAll('canvas'))) {
    if (canvas.width === 0 || canvas.height === 0) continue;
    try {
      const image = target.createElement('img');
      image.src = canvas.toDataURL('image/png');
      image.alt = '';
      image.className = canvas.className;
      image.width = canvas.clientWidth;
      image.height = canvas.clientHeight;
      canvas.replaceWith(image);
    } catch (error) {
      console.warn('Could not snapshot canvas for print:', error);
    }
  }
};

export const createPrintFrame = async ({
  frameClassName,
  bodyClassName,
}: {
  frameClassName?: string;
  bodyClassName?: string;
}): Promise<PrintFrame> => {
  const iframe = document.createElement('iframe');
  iframe.className = frameClassName ?? '';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  iframe.srcdoc = PRINT_FRAME_SRCDOC;

  const loaded = new Promise<void>((resolve) => {
    iframe.addEventListener('load', () => resolve(), { once: true });
  });
  document.body.appendChild(iframe);
  await loaded;

  const frameDocument = iframe.contentDocument;
  if (!frameDocument) {
    iframe.remove();
    throw new Error('Print frame document is unavailable');
  }

  if (document.body.hasAttribute('data-theme')) {
    frameDocument.documentElement.setAttribute('data-theme', 'light');
    frameDocument.body.setAttribute('data-theme', 'light');
  }
  frameDocument.body.className = bodyClassName ?? '';
  const mountNode = frameDocument.createElement('div');
  frameDocument.body.appendChild(mountNode);

  const copiedLinks = new Map<string, HTMLLinkElement>();
  const copiedStyles = new Map<Node | CSSStyleSheet, HTMLStyleElement>();

  const syncStyles = async () => {
    for (const sheet of Array.from(document.styleSheets)) {
      const owner = sheet.ownerNode;
      if (owner instanceof HTMLLinkElement) {
        if (!copiedLinks.has(owner.href)) {
          const link = owner.cloneNode(true) as HTMLLinkElement;
          frameDocument.head.appendChild(link);
          copiedLinks.set(owner.href, link);
        }
        continue;
      }
      const css = serializeRules(sheet);
      if (css === null) continue;
      const key = owner ?? sheet;
      let style = copiedStyles.get(key);
      if (!style) {
        style = frameDocument.createElement('style');
        frameDocument.head.appendChild(style);
        copiedStyles.set(key, style);
      }
      if (style.textContent !== css) style.textContent = css;
    }
    await Promise.all(Array.from(copiedLinks.values()).map(waitForLink));
    await frameDocument.fonts?.ready;
  };

  const print = (title: string) =>
    new Promise<void>((resolve) => {
      const frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        resolve();
        return;
      }
      frameDocument.title = title;
      snapshotCanvases(frameDocument);

      let timer: number | undefined;
      const finish = () => {
        window.clearTimeout(timer);
        frameWindow.removeEventListener('afterprint', finish);
        resolve();
      };
      frameWindow.addEventListener('afterprint', finish);
      timer = window.setTimeout(finish, AFTERPRINT_FALLBACK_MS);
      frameWindow.focus();
      frameWindow.print();
    });

  return {
    document: frameDocument,
    mountNode,
    syncStyles,
    print,
    dispose: () => iframe.remove(),
  };
};
